import React, { useState } from 'react';
import {
  CheckCircleIcon,
  XCircleIcon,
  PencilSquareIcon,
  SparklesIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleSolid } from '@heroicons/react/24/solid';

const AI_ENGINES = [
  { key: 'chatgpt', label: 'ChatGPT', color: 'text-green-600' },
  { key: 'perplexity', label: 'Perplexity', color: 'text-blue-600' },
  { key: 'googleAI', label: 'Google AI', color: 'text-yellow-600' },
  { key: 'copilot', label: 'Copilot', color: 'text-purple-600' },
];

const FILTER_OPTIONS = ['All Prompts', 'With Mentions', 'With Citations', 'No Mentions'];
const DATE_OPTIONS = ['Last 7 days', 'Last 30 days', 'Last 90 days', 'All time'];
const ROWS_PER_PAGE = 5;

const AIPromptVisibility = ({ data = [], loading = false }) => {
  const [filterType, setFilterType] = useState('All Prompts');
  const [dateRange, setDateRange] = useState('Last 30 days');
  const [currentPage, setCurrentPage] = useState(1);

  const filteredData = data.filter((item) => {
    if (filterType === 'With Mentions') return item.mentions > 0;
    if (filterType === 'With Citations') return item.citation;
    if (filterType === 'No Mentions') return item.mentions === 0;
    return true;
  });

  const totalPages = Math.max(1, Math.ceil(filteredData.length / ROWS_PER_PAGE));
  const startIdx = (currentPage - 1) * ROWS_PER_PAGE;
  const pageData = filteredData.slice(startIdx, startIdx + ROWS_PER_PAGE);

  const renderEngineStatus = (isPresent) => {
    if (isPresent) {
      return (
        <CheckCircleSolid
          className="h-5 w-5 text-green-500 mx-auto"
          aria-label="Present"
        />
      );
    }
    return (
      <XCircleIcon
        className="h-5 w-5 text-gray-300 mx-auto"
        aria-label="Not present"
      />
    );
  };

  const renderCitation = (citation) => {
    if (!citation) return <span className="text-gray-400"></span>;
    return (
      <div className="flex items-center justify-center gap-1.5">
        {citation.icon && (
          <img
            src={citation.icon}
            alt={citation.name}
            className="h-4 w-4 rounded"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        )}
        <span className={`text-sm font-medium ${citation.color || 'text-gray-700'}`}>
          {citation.name}
        </span>
        <button
          type="button"
          className="text-gray-400 hover:text-blue-500 transition-colors"
          aria-label={`Edit citation for ${citation.name}`}
        >
          <PencilSquareIcon className="h-3.5 w-3.5" />
        </button>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6" role="status" aria-label="Loading prompt visibility data">
        <div className="animate-pulse space-y-4">
          <div className="flex justify-between items-center">
            <div className="h-6 bg-gray-200 rounded w-48"></div>
            <div className="flex gap-2">
              <div className="h-9 bg-gray-200 rounded w-32"></div>
              <div className="h-9 bg-gray-200 rounded w-32"></div>
            </div>
          </div>
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-12 bg-gray-100 rounded w-full"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-5">
        <h3 className="text-lg font-semibold text-gray-900">
          AI Prompt Visibility
        </h3>
        <div className="flex items-center gap-2">
          <select
            value={filterType}
            onChange={(e) => { setFilterType(e.target.value); setCurrentPage(1); }}
            className="text-sm border border-gray-300 rounded-lg px-3 py-2 bg-white text-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            aria-label="Filter prompt type"
          >
            {FILTER_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="text-sm border border-gray-300 rounded-lg px-3 py-2 bg-white text-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            aria-label="Filter date range"
          >
            {DATE_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto -mx-6 px-6">
        <table className="w-full" role="table" aria-label="AI Prompt Visibility data">
          <thead>
            <tr className="border-b border-gray-200">
              <th scope="col" className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider py-3 pr-4 min-w-[180px]">
                Prompt
              </th>
              {AI_ENGINES.map((engine) => (
                <th key={engine.key} scope="col" className="text-center text-xs font-semibold text-gray-500 uppercase tracking-wider py-3 px-2 w-20">
                  {engine.label}
                </th>
              ))}
              <th scope="col" className="text-center text-xs font-semibold text-gray-500 uppercase tracking-wider py-3 px-2 w-20">
                Mention
              </th>
              <th scope="col" className="text-center text-xs font-semibold text-gray-500 uppercase tracking-wider py-3 pl-2 w-28">
                Citation
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {pageData.length > 0 ? (
              pageData.map((row, idx) => (
                <tr
                  key={row.id || idx}
                  className="hover:bg-gray-50 transition-colors"
                >
                  <td className="py-3.5 pr-4 text-sm font-medium text-gray-900">
                    {row.prompt}
                  </td>
                  {AI_ENGINES.map((engine) => (
                    <td key={engine.key} className="py-3.5 px-2 text-center">
                      {renderEngineStatus(row[engine.key])}
                    </td>
                  ))}
                  <td className="py-3.5 px-2 text-center">
                    {row.mentions > 0 ? (
                      <span className="text-sm font-semibold text-gray-800">
                        {row.mentions}
                      </span>
                    ) : (
                      <span className="text-gray-400"></span>
                    )}
                  </td>
                  <td className="py-3.5 pl-2 text-center">
                    {renderCitation(row.citation)}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={7} className="py-16 text-center">
                  <SparklesIcon className="h-10 w-10 text-gray-300 mx-auto mb-3" />
                  <p className="text-sm font-medium text-gray-500">
                    No prompt data available yet.
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    Add prompts to start tracking your AI visibility.
                  </p>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {filteredData.length > ROWS_PER_PAGE && (
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
          <p className="text-xs text-gray-500">
            Showing {startIdx + 1}{Math.min(startIdx + ROWS_PER_PAGE, filteredData.length)} of {filteredData.length} prompts
          </p>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              aria-label="Previous page"
            >
              <ChevronLeftIcon className="h-4 w-4" />
            </button>
            {[...Array(totalPages)].map((_, i) => (
              <button
                key={i + 1}
                type="button"
                onClick={() => setCurrentPage(i + 1)}
                className={`min-w-[32px] h-8 rounded-lg text-sm font-medium transition-colors ${
                  currentPage === i + 1
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
                aria-label={`Page ${i + 1}`}
                aria-current={currentPage === i + 1 ? 'page' : undefined}
              >
                {i + 1}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              aria-label="Next page"
            >
              <ChevronRightIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIPromptVisibility;
